import { useQuery } from '@tanstack/react-query'
import api from '../../config/axios-config'
import { tasksQueriesKeys } from '../constants.js'

/**
 * Fetches a paginated task list for a project.
 *
 * @param {Object} params - Query parameters.
 * @param {string} params.projectId - Project identifier used in the tasks endpoint.
 * @param {string} [params.search=''] - Search term used to filter the task list.
 * @param {number} [params.size=25] - Number of tasks requested per page.
 * @param {number} [params.page=0] - Zero-based page index.
 * @param {string} [params.sort='Title'] - Field used to sort the task list.
 * @param {string} [params.direction='DESC'] - Sort direction sent to the API.
 * @param {string} [params.filter='Any'] - Task filter sent to the API.
 * @param {string} [params.stateFilter='All'] - Task state filter sent to the API.
 * @param {string} [params.filterId=''] - Saved filter identifier sent to the API.
 * @param {Object} [params.options={}] - Additional React Query options.
 * @returns {import('@tanstack/react-query').UseQueryResult} React Query result with the task list response.
 */

export const useGetTaskListQuery = ({
  projectId,
  search = '',
  size = 25,
  page = 0,
  sort = 'Title',
  direction = 'DESC',
  filter = 'Any',
  stateFilter = 'All',
  filterId = '',
  options = {}
} = {}) => {
  const handleQuery = async () => {
    const config = {
      method: 'GET',
      params: {
        size,
        page,
        sort,
        direction,
        search,
        filter,
        stateFilter,
        filterId
      }
    }

    const { data } = await api(`/api/projects/${projectId}/tasks`, config)

    return data
  }

  return useQuery({
    queryKey: [
      tasksQueriesKeys.GET_TASK_LIST,
      projectId,
      size,
      page,
      sort,
      direction,
      search,
      filter,
      stateFilter,
      filterId
    ],
    queryFn: handleQuery,
    enabled: !!projectId,
    ...options
  })
}
